import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Space,
  Typography,
  message,
  Table,
  Tag,
  Tabs,
  Switch,
  Popconfirm,
} from 'antd';
import {
  SaveOutlined,
  ReloadOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { organizationService, permissionService, approvalService } from '../services/api';
import { usePermissions } from '../contexts/PermissionContext';

const { Title, Text } = Typography;

const ROLE_COLORS = {
  admin: 'red',
  manager: 'blue',
  analyst: 'green',
  finance: 'gold',
  viewer: 'default',
};

const OrgSettings = () => {
  const [organization, setOrganization] = useState(null);
  const [permissionMatrix, setPermissionMatrix] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const { can, refreshPermissions } = usePermissions();

  const canEdit = can('organization', 'edit');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [orgData, matrixData, workflowData] = await Promise.all([
        organizationService.getMyOrganization(),
        permissionService.getPermissionMatrix(),
        approvalService.getWorkflows(true),
      ]);
      setOrganization(orgData);
      setPermissionMatrix(matrixData);
      setWorkflows(workflowData);
      form.setFieldsValue(orgData);
    } catch (error) {
      message.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSaveOrg = async (values) => {
    try {
      await organizationService.update(organization.id, values);
      message.success('Organization updated');
      fetchData();
    } catch (error) {
      message.error('Failed to update organization');
    }
  };

  const handleTogglePermission = async (role, resource, action, currentValue) => {
    try {
      await permissionService.createOverride({
        role,
        resource,
        action,
        is_allowed: !currentValue,
      });
      message.success('Permission updated');
      fetchData();
      refreshPermissions();
    } catch (error) {
      message.error('Failed to update permission');
    }
  };

  const handleToggleWorkflow = async (workflowId, isActive) => {
    try {
      await approvalService.updateWorkflow(workflowId, { is_active: isActive });
      message.success('Workflow updated');
      fetchData();
    } catch (error) {
      message.error('Failed to update workflow');
    }
  };

  const handleDeleteWorkflow = async (workflowId) => {
    try {
      await approvalService.deleteWorkflow(workflowId);
      message.success('Workflow deleted');
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to delete workflow');
    }
  };

  // Build permission table columns dynamically
  const buildPermissionColumns = () => {
    if (permissionMatrix.length === 0) return [];

    // Get all unique resources and actions
    const allResources = new Set();
    permissionMatrix.forEach((rolePerms) => {
      Object.keys(rolePerms.permissions).forEach((resource) => {
        allResources.add(resource);
      });
    });

    return [
      {
        title: 'Role',
        dataIndex: 'role',
        key: 'role',
        fixed: 'left',
        width: 100,
        render: (role) => (
          <Tag color={ROLE_COLORS[role]}>{role?.toUpperCase()}</Tag>
        ),
      },
      ...Array.from(allResources).map((resource) => ({
        title: resource.replace('_', ' ').toUpperCase(),
        key: resource,
        children: ['view', 'edit', 'delete', 'import', 'download'].filter((action) =>
          permissionMatrix.some(
            (rm) => rm.permissions[resource] && rm.permissions[resource][action] !== undefined
          )
        ).map((action) => ({
          title: action,
          key: `${resource}_${action}`,
          width: 80,
          render: (_, record) => {
            const hasPermission = record.permissions[resource]?.[action] === true;
            return canEdit ? (
              <Switch
                size="small"
                checked={hasPermission}
                onChange={() =>
                  handleTogglePermission(record.role, resource, action, hasPermission)
                }
                disabled={record.role === 'admin'}
              />
            ) : (
              <Tag color={hasPermission ? 'green' : 'red'}>
                {hasPermission ? 'Yes' : 'No'}
              </Tag>
            );
          },
        })),
      })),
    ];
  };

  const workflowColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Entity',
      dataIndex: 'entity_type',
      key: 'entity_type',
      render: (type) => type?.toUpperCase(),
    },
    {
      title: 'Trigger',
      dataIndex: 'trigger_action',
      key: 'trigger_action',
      render: (action) => action?.toUpperCase(),
    },
    {
      title: 'Requires',
      key: 'requires',
      render: (_, record) => (
        <Space>
          {record.require_manager_approval && <Tag color="blue">Manager</Tag>}
          {record.require_finance_approval && <Tag color="gold">Finance</Tag>}
        </Space>
      ),
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive, record) =>
        canEdit ? (
          <Switch
            checked={isActive}
            onChange={(checked) => handleToggleWorkflow(record.id, checked)}
          />
        ) : (
          <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'Active' : 'Inactive'}</Tag>
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) =>
        canEdit && (
          <Popconfirm
            title="Delete this workflow?"
            onConfirm={() => handleDeleteWorkflow(record.id)}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        ),
    },
  ];

  const tabItems = [
    {
      key: 'general',
      label: 'General',
      children: (
        <Card>
          <Form form={form} layout="vertical" onFinish={handleSaveOrg}>
            <Form.Item name="name" label="Organization Name" rules={[{ required: true }]}>
              <Input disabled={!canEdit} />
            </Form.Item>
            <Form.Item name="domain" label="Domain">
              <Input disabled={!canEdit} placeholder="example.com" />
            </Form.Item>
            <Form.Item name="industry" label="Industry">
              <Select disabled={!canEdit}>
                <Select.Option value="technology">Technology</Select.Option>
                <Select.Option value="finance">Finance</Select.Option>
                <Select.Option value="healthcare">Healthcare</Select.Option>
                <Select.Option value="retail">Retail</Select.Option>
                <Select.Option value="manufacturing">Manufacturing</Select.Option>
                <Select.Option value="other">Other</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item name="size" label="Company Size">
              <Select disabled={!canEdit}>
                <Select.Option value="1-50">1-50 employees</Select.Option>
                <Select.Option value="51-200">51-200 employees</Select.Option>
                <Select.Option value="201-1000">201-1000 employees</Select.Option>
                <Select.Option value="1000+">1000+ employees</Select.Option>
              </Select>
            </Form.Item>
            {canEdit && (
              <Form.Item>
                <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                  Save Changes
                </Button>
              </Form.Item>
            )}
          </Form>
        </Card>
      ),
    },
    {
      key: 'permissions',
      label: 'Role Permissions',
      children: (
        <Card>
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">
              Customize what each role can access. Admin role has full access and cannot be modified.
            </Text>
          </div>
          <Table
            columns={buildPermissionColumns()}
            dataSource={permissionMatrix}
            rowKey="role"
            scroll={{ x: 'max-content' }}
            pagination={false}
            size="small"
            loading={loading}
          />
        </Card>
      ),
    },
    {
      key: 'workflows',
      label: 'Approval Workflows',
      children: (
        <Card>
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">
              Configure which actions require approval before being applied.
            </Text>
          </div>
          <Table
            columns={workflowColumns}
            dataSource={workflows}
            rowKey="id"
            pagination={false}
            loading={loading}
          />
        </Card>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Title level={4} style={{ margin: 0 }}>Organization Settings</Title>
        <Button icon={<ReloadOutlined />} onClick={fetchData}>
          Refresh
        </Button>
      </div>

      <Tabs items={tabItems} />
    </div>
  );
};

export default OrgSettings;
